import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, GitMerge, Loader2, CheckCircle } from 'lucide-react';

interface MergeResult {
  story?: string;        // for user stories
  title?: string;        // for NFRs
  description?: string;  // for NFRs
  category?: string;     // for NFRs
  acceptanceCriteria: string[];
  priority: 'high' | 'medium' | 'low';
  reasoning?: string;
}

interface MergePreviewModalProps {
  type: 'story' | 'nfr';
  selectedCount: number;
  loading: boolean;
  result: MergeResult | null;
  error?: string | null;
  onConfirm: (edited: MergeResult) => Promise<void>;
  onClose: () => void;
}

export function MergePreviewModal({ type, selectedCount, loading, result, error, onConfirm, onClose }: MergePreviewModalProps) {
  const [edited, setEdited] = useState<MergeResult | null>(null);
  const [saving, setSaving] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result && !edited) setEdited({ ...result, acceptanceCriteria: [...result.acceptanceCriteria] });
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleConfirm = async () => {
    if (!edited) return;
    setSaving(true);
    try {
      await onConfirm(edited);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const updateAC = (i: number, val: string) =>
    setEdited(prev => prev ? { ...prev, acceptanceCriteria: prev.acceptanceCriteria.map((x, xi) => xi === i ? val : x) } : null);
  const removeAC = (i: number) =>
    setEdited(prev => prev ? { ...prev, acceptanceCriteria: prev.acceptanceCriteria.filter((_, xi) => xi !== i) } : null);
  const addAC = () =>
    setEdited(prev => prev ? { ...prev, acceptanceCriteria: [...prev.acceptanceCriteria, ''] } : null);

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-primary" />
            <h2 className="text-base font-bold text-navy">
              Merge {selectedCount} {type === 'story' ? 'Stories' : 'NFRs'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-500">
              <Loader2 size={28} className="animate-spin text-primary" />
              <p className="text-sm font-medium">AI is merging…</p>
            </div>
          )}

          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600">{error}</div>
          )}

          {!loading && !error && edited && (
            <div className="flex flex-col gap-5">
              {/* Reasoning */}
              {edited.reasoning && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">AI reasoning</p>
                  <p className="text-xs text-amber-700">{edited.reasoning}</p>
                </div>
              )}

              {/* Story text (stories only) */}
              {type === 'story' && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Merged Story</label>
                  <textarea
                    value={edited.story || ''}
                    onChange={e => setEdited(prev => prev ? { ...prev, story: e.target.value } : null)}
                    rows={4}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                  />
                </div>
              )}

              {/* NFR fields */}
              {type === 'nfr' && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Title</label>
                    <input
                      value={edited.title || ''}
                      onChange={e => setEdited(prev => prev ? { ...prev, title: e.target.value } : null)}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
                    <textarea
                      value={edited.description || ''}
                      onChange={e => setEdited(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
                      <select
                        value={edited.category || ''}
                        onChange={e => setEdited(prev => prev ? { ...prev, category: e.target.value } : null)}
                        className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white capitalize"
                      >
                        {['performance','security','scalability','availability','usability','maintainability','compliance'].map(c => (
                          <option key={c} value={c} className="capitalize">{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
                      <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
                        {(['high', 'medium', 'low'] as const).map(p => (
                          <button key={p} type="button" onClick={() => setEdited(prev => prev ? { ...prev, priority: p } : null)}
                            className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${edited.priority === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Priority for stories */}
              {type === 'story' && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
                  <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-48">
                    {(['high', 'medium', 'low'] as const).map(p => (
                      <button key={p} type="button" onClick={() => setEdited(prev => prev ? { ...prev, priority: p } : null)}
                        className={`flex-1 py-2 text-xs font-semibold transition-all capitalize ${edited.priority === p ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Acceptance criteria */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <CheckCircle size={11} />Acceptance Criteria
                </label>
                <div className="flex flex-col gap-1.5">
                  {edited.acceptanceCriteria.map((ac, i) => (
                    <div key={ac || `ac-${i}`} className="flex items-center gap-2"> {/* NOSONAR */}
                      <input
                        value={ac}
                        onChange={e => updateAC(i, e.target.value)}
                        className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                      <button onClick={() => removeAC(i)}
                        className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <button onClick={addAC}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-semibold transition-colors w-fit">
                    <Plus size={11} /> Add criterion
                  </button>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                Confirming will save the merged {type === 'story' ? 'story' : 'NFR'} and delete the {selectedCount} originals.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && edited && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
            <button onClick={onClose}
              className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={saving}
              className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
              <GitMerge size={14} />
              {saving ? 'Saving…' : 'Confirm Merge'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
